/**
 * Modelo de domínio da feature **Configuração de Nota e Frequência**.
 *
 * A origem remota (tag `class-planning-grade-and-frequence-configuration` + dropdowns de
 * `class-planning-shared`) é servida pelos tipos gerados pelo Kubb, reexportados abaixo e
 * usados na camada de API/adaptador. Os tipos **UI-only** desta feature (os quatro tipos de
 * atividade, controles por avaliação e nota/frequência padrão) permanecem locais porque o
 * backend ainda não os expõe — ver `utils/adaptGradeAndFrequence.ts`.
 */

// ── Tipos gerados pelo Kubb (fonte de verdade dos dados remotos) ────────────────
// Enums gerados — priorizados sobre uniões de string escritas à mão.
import type { EClassRoomDisciplinesStatus } from '@/shared/api/generated';

export type {
  ClassroomDisciplineIdsViewModel,
  DropDownListViewModel,
  EClassRoomDisciplinesStatus,
  EClassRoomUserType,
  EClassType,
  GradeAndFrequenceViewModel,
  GradeFrequenceClassViewModel,
  GradeFrequenceDisciplineViewModel,
  GradeFrequenceExamInfoViewModel,
  GradeFrequenceModuleViewModel,
} from '@/shared/api/generated';

/**
 * Tipo de item que pode ser vinculado a um módulo (**UI-only**).
 *
 * Sem contrapartida no backend atual (`GradeAndFrequenceViewModel` só modela disciplinas);
 * o prefixo `E` é exigido pelo Biome. Os **valores** são usados como chaves de i18n
 * (`types.*`/`typeFilters.*`) e por isso são preservados.
 */
export enum EActivityType {
  Discipline = 'disciplina',
  FinalWorkStage = 'tcc_etapa',
  FinalExam = 'prova_final',
  FinalWork = 'tcc_final',
}

/**
 * Forma de fechamento de uma atividade: por disciplina (provas por aula somam 100%)
 * ou no curso (item único de 100%).
 */
export type EClosingType = 'disciplina' | 'curso';

/** Prova/avaliação de uma aula. O `weight` compõe os 100% da disciplina. */
export interface Exam {
  readonly id: string;
  readonly name: string;
  /** Peso da prova dentro da disciplina (0–100). */
  readonly weight: number;
}

/** Aula de uma disciplina, com sua prova principal e flags de documento/cálculo. */
export interface Lesson {
  readonly id: string;
  readonly name: string;
  /** Data ISO (`YYYY-MM-DD`). */
  readonly date: string;
  /** Situação da aula retornada pelo backend (1 Planejado | 2 Finalizado | 3 Cancelado | 4 Confirmado). */
  readonly status?: EClassRoomDisciplinesStatus;
  readonly showInDocument: boolean;
  readonly countsInGrade: boolean;
  readonly countsInAttendance: boolean;
  readonly attendanceByView: boolean;
  readonly exam: Exam;
}

/** Disciplina da turma, com período e a lista de aulas. */
export interface Discipline {
  readonly id: string;
  readonly name: string;
  /** Data ISO de início (`YYYY-MM-DD`). */
  readonly startDate: string;
  /** Data ISO de fim (`YYYY-MM-DD`). */
  readonly endDate: string;
  readonly lessons: readonly Lesson[];
}

/**
 * Módulo do curso.
 *
 * Os módulos **não possuem peso** no momento (conceito removido da UI/estado — ver a change
 * `grade-config-empty-state`). A média final é calculada sem ponderação por módulo.
 */
export interface Module {
  readonly id: string;
  readonly name: string;
}

/**
 * Vínculo de um item (disciplina/etapa/prova/trabalho final) a um módulo.
 *
 * `refId`/`moduleId` podem ser `null` (item ainda não selecionado / sem módulo).
 */
export interface Activity {
  readonly id: string;
  readonly type: EActivityType;
  readonly refId: string | null;
  readonly moduleId: string | null;
  readonly showInDocument: boolean;
  readonly countsInGrade: boolean;
  readonly countsInAttendance: boolean;
  /** Permanece no modelo, mas não é mais alternável na UI (decisão do designer). */
  readonly attendanceByView: boolean;
  readonly closing: EClosingType;
  /** Peso da atividade dentro do módulo (0–100). */
  readonly moduleWeight: number;
  /** Permanece no modelo, mas não é mais editável na UI (decisão do designer). */
  readonly defaultGrade: number | null;
  /** Permanece no modelo, mas não é mais editável na UI (decisão do designer). */
  readonly defaultAttendance: number | null;
}

/** Turma de trabalho: disciplinas disponíveis, módulos e vínculos configurados. */
export interface ClassGroup {
  readonly id: string;
  readonly name: string;
  readonly course: string;
  readonly disciplines: readonly Discipline[];
  readonly modules: readonly Module[];
  readonly activities: readonly Activity[];
}

/**
 * Item referenciável por uma atividade (disciplina, etapa de TCC, prova final ou
 * opção fixa de trabalho final). Disciplinas carregam `lessons`; os demais não.
 */
export interface GradeRefItem {
  readonly id: string;
  readonly name: string;
  readonly lessons?: readonly Lesson[];
}
